/* Shared storefront experiments. No dependencies; never blocks a cart operation on analytics. */
(function(global, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else if (!global.LobelsExperiments) global.LobelsExperiments = factory(global);
})(typeof window !== 'undefined' ? window : this, function(win) {
  'use strict';

  var doc = win.document;
  var prefix = 'lobels_ab_v1:';
  var visitorKey = prefix + 'visitor';
  var controllers = new Map();
  var pendingMounts = new WeakMap();
  var cartQueue = Promise.resolve();
  var privacyPromise;
  var day = 86400000;

  function consentAllowed() {
    try {
      return win.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
    } catch (error) { return false; }
  }

  function readyPrivacy() {
    if (win.Shopify && win.Shopify.customerPrivacy) return Promise.resolve();
    if (!privacyPromise) privacyPromise = new Promise(function(resolve) {
      var timer = win.setTimeout(resolve, 600);
      try {
        win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function() {
          win.clearTimeout(timer);
          resolve();
        });
      } catch (error) { win.clearTimeout(timer); resolve(); }
    });
    return privacyPromise;
  }

  function normalize(raw) {
    var statuses = ['disabled', 'running', 'a', 'b'];
    var behaviors = ['unchanged', 'none', 'shine', 'peek'];
    var id = String(raw.id || '');
    var version = String(raw.version || '');
    var valid = /^[a-z0-9][a-z0-9-]{0,47}$/.test(id) && /^[a-z0-9][a-z0-9-]{0,23}$/.test(version);
    return {
      id: id, version: version,
      status: valid && statuses.indexOf(raw.status) !== -1 ? raw.status : 'disabled',
      trafficA: Math.max(0, Math.min(100, Number.isFinite(Number(raw.trafficA)) ? Number(raw.trafficA) : 50)),
      audience: raw.audience === 'all' ? 'all' : 'desktop',
      preview: raw.preview === 'b' ? 'b' : raw.preview === 'a' ? 'a' : 'auto',
      editor: !!(raw.editor || raw.designMode || (win.Shopify && win.Shopify.designMode)),
      attributionDays: Math.max(1, Math.min(30, Number(raw.attributionDays) || 7)),
      bundleHandle: String(raw.bundleHandle || ''),
      targetSelector: raw.targetSelector || '',
      behaviors: {
        a: behaviors.indexOf(raw.behaviors && raw.behaviors.a) !== -1 ? raw.behaviors.a : 'unchanged',
        b: behaviors.indexOf(raw.behaviors && raw.behaviors.b) !== -1 ? raw.behaviors.b : 'unchanged'
      }
    };
  }

  function storageKey(config) { return prefix + config.id + ':' + config.version; }
  function cartKey(record) { return '__lobels_ab_' + record.experiment_id + '_' + record.experiment_version; }

  function validRecord(record) {
    return record && /^[a-z0-9][a-z0-9-]{0,47}$/.test(record.experiment_id) &&
      /^[a-z0-9][a-z0-9-]{0,23}$/.test(record.experiment_version) &&
      /^[a-f0-9]{32}$/.test(record.assignment_id) && ['a', 'b'].indexOf(record.variant) !== -1 &&
      Number.isFinite(Date.parse(record.assigned_at)) && Date.parse(record.assigned_at) <= Date.now() && Date.now() - Date.parse(record.assigned_at) < 90 * day;
  }

  function activeExposure(record) {
    return validRecord(record) && Number.isFinite(Date.parse(record.exposed_at)) &&
      Date.parse(record.exposed_at) <= Date.now() && Date.parse(record.expires_at) > Date.now();
  }

  function randomId() {
    var bytes = new Uint8Array(16);
    win.crypto.getRandomValues(bytes);
    return Array.prototype.map.call(bytes, function(value) { return value.toString(16).padStart(2, '0'); }).join('');
  }

  function bucket(seed) {
    var hash = 2166136261;
    for (var i = 0; i < seed.length; i++) hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619);
    return (hash >>> 0) / 4294967296 * 100;
  }

  function read(config) {
    try {
      var record = JSON.parse(win.localStorage.getItem(storageKey(config)) || 'null');
      return validRecord(record) && record.experiment_id === config.id && record.experiment_version === config.version ? record : null;
    } catch (error) { return null; }
  }

  function eligible(config) {
    if (config.audience === 'desktop' && !win.matchMedia('(min-width: 1321px)').matches) return false;
    // Visitors whose motion preference erases the tested difference aren't enrolled.
    return config.behaviors.a === config.behaviors.b || !win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function publish(name, payload) {
    if (!consentAllowed()) return false;
    try {
      if (!win.Shopify.analytics || typeof win.Shopify.analytics.publish !== 'function') return false;
      win.Shopify.analytics.publish('lobels_ab:' + name, payload);
      return true;
    } catch (error) { return false; }
  }

  function payload(record) {
    return {
      experiment_id: record.experiment_id, experiment_version: record.experiment_version,
      variant: record.variant, assignment_id: record.assignment_id,
      assigned_at: record.assigned_at, exposed_at: record.exposed_at,
      expires_at: record.expires_at, bundle_handle: record.bundle_handle
    };
  }

  function collectCartAttributes(clear) {
    var attributes = {};
    try {
      for (var i = 0; i < win.localStorage.length; i++) {
        var key = win.localStorage.key(i);
        if (!key || key.indexOf(prefix) !== 0 || key === visitorKey) continue;
        var record;
        try { record = JSON.parse(win.localStorage.getItem(key)); } catch (error) { continue; }
        if (!record || !/^[a-z0-9-]+$/.test(record.experiment_id) || !/^[a-z0-9-]+$/.test(record.experiment_version)) continue;
        // Empty values remove expired attribution; unrelated cart attributes are untouched.
        attributes[cartKey(record)] = !clear && activeExposure(record) ? JSON.stringify(payload(record)) : '';
      }
    } catch (error) { /* Storage restrictions must never prevent a purchase. */ }
    return attributes;
  }

  function writeCartAttributes(attributes) {
    if (!Object.keys(attributes).length) return Promise.resolve(true);
    return new Promise(function(resolve) {
      var abort = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      var timer = win.setTimeout(function() { if (abort) abort.abort(); resolve(false); }, 1200);
      var cartRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
      try {
        Promise.resolve(win.fetch(cartRoot + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: attributes }), signal: abort ? abort.signal : undefined
        })).then(function(response) { win.clearTimeout(timer); resolve(response.ok); }, function() { win.clearTimeout(timer); resolve(false); });
      } catch (error) { win.clearTimeout(timer); resolve(false); }
    });
  }

  function persistCartAttributes() {
    // Serialize our own writes. Callers can await a bounded attempt before checkout.
    cartQueue = cartQueue.catch(function() {}).then(function() {
      return writeCartAttributes(collectCartAttributes(!consentAllowed()));
    });
    return cartQueue;
  }

  function applyBlocks(root, variant) {
    root.dataset.abSelected = variant;
    Array.prototype.forEach.call(root.querySelectorAll('[data-ab-variant]'), function(node) {
      var choice = node.getAttribute('data-ab-variant');
      node.hidden = choice !== 'all' && choice !== variant;
      node.toggleAttribute('inert', node.hidden);
    });
    root.removeAttribute('data-ab-pending');
  }

  function mount(root) {
    if (pendingMounts.has(root)) return pendingMounts.get(root);
    var pending = initialize(root).finally(function() { pendingMounts.delete(root); });
    pendingMounts.set(root, pending);
    return pending;
  }

  async function initialize(root) {
    if (controllers.has(root)) return controllers.get(root);
    var node = root.querySelector('script[data-ab-config]');
    if (!node) return null;
    var raw;
    try { raw = JSON.parse(node.textContent); } catch (error) { return null; }
    var config = normalize(raw);
    var record = null;
    var variant = config.status === 'b' ? 'b' : 'a';
    var tracking = false;
    var destroyed = false;
    var published = false;
    var observer;
    var retryTimer;
    var retries = 0;
    var editing = new URLSearchParams(win.location.search).get('rc_edit') === '1';
    // Never use arbitrary URL overrides in measured traffic.
    if (config.editor) {
      if (config.preview !== 'auto') variant = config.preview;
    } else if (config.status === 'running') {
      await readyPrivacy();
      // The Liquid fallback may already have revealed A after a slow asset/API load.
      // Keep that interface stable and exclude this visit rather than flash A then B.
      if (root.dataset.abFallback === 'true') config.status = 'disabled';
      if (config.status === 'running' && consentAllowed()) {
        record = read(config);
        if (record && (editing || eligible(config))) variant = record.variant;
        if (!editing && eligible(config)) {
          try {
            if (!record) {
              var visitor = win.localStorage.getItem(visitorKey);
              if (!/^[a-f0-9]{32}$/.test(visitor || '')) {
                visitor = randomId();
                win.localStorage.setItem(visitorKey, visitor);
              }
              variant = bucket(visitor + ':' + config.id + ':' + config.version) < config.trafficA ? 'a' : 'b';
              record = {
                experiment_id: config.id, experiment_version: config.version, variant: variant,
                assignment_id: randomId(), assigned_at: new Date().toISOString(),
                exposed_at: null, expires_at: null, bundle_handle: config.bundleHandle
              };
              win.localStorage.setItem(storageKey(config), JSON.stringify(record));
            }
            tracking = true;
          } catch (error) { variant = 'a'; record = null; }
        }
      }
    }

    // Forced winners and previews are presentation only, never enrolled in metrics.
    var behavior = config.status === 'disabled' && (!config.editor || config.preview === 'auto') ? 'unchanged' : config.behaviors[variant];
    var controller = {
      config: config, variant: variant, behavior: behavior,
      expose: function() {
        if (destroyed || published || !tracking || !root.isConnected || doc.hidden || !consentAllowed() || !eligible(config)) return false;
        if (!record.exposed_at) {
          record.exposed_at = new Date().toISOString();
          record.expires_at = new Date(Date.now() + config.attributionDays * day).toISOString();
          try { win.localStorage.setItem(storageKey(config), JSON.stringify(record)); } catch (error) { tracking = false; return false; }
        }
        if (!activeExposure(record)) return false;
        published = publish('exposure', payload(record));
        if (published) persistCartAttributes();
        return published;
      },
      track: function(action) {
        if (!tracking || destroyed || !consentAllowed()) return false;
        observeVisibility();
        if (!activeExposure(record)) return false;
        return publish('action', Object.assign(payload(record), { action: String(action).slice(0, 64) }));
      },
      destroy: function() {
        destroyed = true;
        if (observer) observer.disconnect();
        win.clearTimeout(retryTimer);
        doc.removeEventListener('visibilitychange', observeVisibility);
        win.removeEventListener('resize', observeVisibility);
        win.removeEventListener('scroll', observeVisibility);
        controllers.delete(root);
      }
    };
    controllers.set(root, controller);
    applyBlocks(root, variant);
    var target;
    try { target = config.targetSelector ? root.querySelector(config.targetSelector) || root : root; } catch (error) { target = root; }
    function observeVisibility() {
      if (destroyed || !root.isConnected) { controller.destroy(); return; }
      var rect = target.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < win.innerHeight && rect.width > 0 && rect.height > 0) {
        controller.expose();
        // Shopify's event bus may finish loading after the component is visible.
        if (!published && retries < 6) {
          win.clearTimeout(retryTimer);
          retries++;
          retryTimer = win.setTimeout(observeVisibility, 500);
        }
      }
    }
    if (tracking) {
      if (typeof win.IntersectionObserver === 'function') {
        observer = new win.IntersectionObserver(function(entries) {
          if (entries.some(function(entry) { return entry.isIntersecting; })) observeVisibility();
        });
        observer.observe(target);
      } else {
        win.addEventListener('scroll', observeVisibility, { passive: true });
      }
      doc.addEventListener('visibilitychange', observeVisibility);
      win.addEventListener('resize', observeVisibility);
      observeVisibility();
    }
    return controller;
  }

  doc.addEventListener('visitorConsentCollected', function() {
    if (consentAllowed()) return; // Enroll on the next page load; don't swap a working interface mid-visit.
    var attributes = collectCartAttributes(true);
    controllers.forEach(function(controller) { controller.destroy(); });
    try {
      var keys = [];
      for (var i = 0; i < win.localStorage.length; i++) if ((win.localStorage.key(i) || '').indexOf(prefix) === 0) keys.push(win.localStorage.key(i));
      keys.forEach(function(key) { win.localStorage.removeItem(key); });
    } catch (error) { /* Nothing to clear when storage is unavailable. */ }
    cartQueue = cartQueue.catch(function() {}).then(function() { return writeCartAttributes(attributes); });
  });
  doc.addEventListener('shopify:section:unload', function(event) {
    controllers.forEach(function(controller, root) { if (event.target.contains(root)) controller.destroy(); });
  });

  // Loaded globally so expiry/consent cleanup also runs on cart and other theme pages.
  async function refreshStoredAttribution() {
    var hasRecords = false;
    try {
      for (var i = 0; i < win.localStorage.length; i++) {
        var key = win.localStorage.key(i);
        if (key && key.indexOf(prefix) === 0 && key !== visitorKey) { hasRecords = true; break; }
      }
    } catch (error) { return; }
    if (!hasRecords) return;
    await readyPrivacy();
    await persistCartAttributes();
  }
  if (doc.readyState === 'complete') refreshStoredAttribution();
  else doc.addEventListener('DOMContentLoaded', refreshStoredAttribution, { once: true });

  return { mount: mount, persistCartAttributes: persistCartAttributes, destroy: function(root) {
    if (controllers.has(root)) controllers.get(root).destroy();
  } };
});
